package dev.lingvo.site.pages.app

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import dev.lingvo.shared.MentionDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LTimeStyle
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.formatUtc
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Every mention, in full.
 *
 * The "see all" behind the queue's Mentions section, and deliberately **not a second queue**. The
 * moment this page grows its own opinion about what matters, there are two answers to "what should I
 * do now" and a translator has to work out which one is authoritative — which is the exact problem
 * `/app/work` exists to end.
 */
@Page("/app/inbox")
@Layout(".components.layouts.AppLayout")
@Composable
fun InboxPage() {
    val ctx = rememberPageContext()
    var refreshKey by remember { mutableStateOf(0) }
    val state = loadAsync(refreshKey) { ApiClient.mentions() }
    // The server matches a comment body against the reader's **display name** — there is no handle
    // column — so the empty state has to show the string people actually have to type.
    val myName = (Session.state as? SessionState.Authenticated)?.user?.name?.takeIf { it.isNotBlank() }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.inbox.inbox", "Inbox"),
            t("app.inbox.unresolved_comments_that_name_you_newest", "Unresolved comments that name you, newest first."),
        )

        LAsyncContent(
            state,
            rows = 6,
            loadingLabel = t("app.inbox.loading_mentions", "Loading mentions"),
            onRetry = { refreshKey++ },
        ) { mentions ->
            if (mentions.isEmpty()) {
                LEmptyState(
                    t("app.inbox.nobody_has_asked_you_anything", "Nobody has asked you anything"),
                    // It used to say "@your-name", which is not a string anybody can look up: it is
                    // the reader's display name, spaces and all, and renaming themselves silently
                    // stops every existing mention matching.
                    if (myName != null) {
                        tf(
                            "app.inbox.when_somebody_writes_name_in_a",
                            "When somebody writes {name} in a comment it will appear here. It is " +
                            "matched against your display name, so changing that changes what " +
                            "people have to type.", "name" to "@$myName")
                    } else {
                        t("app.inbox.a_comment_that_names_you_will", "A comment that names you will appear here.")
                    },
                    Modifier.fillMaxWidth(),
                    LEmptyStateIcon.INBOX,
                ) {
                    LButton(
                        t("app.inbox.open_your_queue", "Open your queue"),
                        { ctx.router.tryRoutingTo("/app/work") },
                        variant = LButtonVariant.SECONDARY,
                    )
                }
            } else {
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth()) {
                        mentions.forEachIndexed { index, mention ->
                            MentionRow(mention)
                            if (index != mentions.lastIndex) LDivider()
                        }
                    }
                }
            }
        }
    }
}

/**
 * One mention, extracted so its geometry can be measured.
 *
 * The key line is a monospace `project · key`, and a translation key is long, dotted and offers no
 * break opportunity — the shape that overflows a phone if the row cannot wrap and the text cannot
 * break. `InboxGeometryTest` pins it at the required widths.
 */
@Composable
internal fun MentionRow(mention: MentionDto) {
    Column(Modifier.fillMaxWidth().gap(Space.XS).attr("data-mention-row", "")) {
        Row(
            Modifier.fillMaxWidth().gap(Space.SM),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // A flex item's default `min-width` is `auto`, so this would not shrink below its own
            // content. In a row that cannot wrap, one ordinary key pushed the mention off the phone.
            Span(
                Modifier
                    .flexGrow(1)
                    .minWidth(0.px)
                    .monoFont()
                    .fontSize(TypeScale.Small)
                    .styleModifier { property("overflow-wrap", "anywhere") }
                    .toAttrs(),
            ) {
                Text(tf("app.inbox.project_and_key", "{project} · {key}", "project" to mention.projectName, "key" to mention.keyName))
            }
            // One of these per mention, so unlabelled they are a list of identical "Open" links.
            // The key beside it is sibling text, not part of the link's name.
            Link(
                path = mention.href,
                modifier = Modifier.attr(
                    "aria-label",
                    trf(
                        "app.inbox.open_project_key",
                        "Open {project} {key}",
                        "project" to mention.projectName,
                        "key" to mention.keyName,
                    ),
                ),
            ) { Text(t("app.inbox.open", "Open")) }
        }
        // Somebody else's words, rendered as text and never as markup.
        Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) { Text(mention.body) }
        // The age, the way the work log and the activity page say it. A bare `2026-09-02` is a
        // machine's format for a fact a reader is comparing against "this morning".
        LMuted(
            listOfNotNull(mention.author, formatRelativeTime(mention.at)).joinToString(" · "),
            Modifier.attr("title", formatUtc(mention.at, LTimeStyle.STAMP)),
        )
    }
}
